import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials, faqs } from '@/mocks/testimonials';

const platformStats = [
  {
    label: '收录漫画',
    value: '2,000+',
    icon: 'ri-book-open-line',
    desc: '涵盖热血、玄幻、修真等十余种类型',
  },
  {
    label: '累计读者',
    value: '5,000万+',
    icon: 'ri-user-follow-line',
    desc: '每日活跃读者超过 80 万人次',
  },
  {
    label: '日更新章节',
    value: '300+',
    icon: 'ri-calendar-check-line',
    desc: '日均更新 300 余话，每周新增作品',
  },
  {
    label: '运营天数',
    value: '2,000+',
    icon: 'ri-time-line',
    desc: '自 2020 年起稳定运营至今',
  },
];

const cooperationItems = [
  {
    title: '漫画授权合作',
    desc: '欢迎漫画作者、工作室及版权方与我们洽谈作品授权事宜，我们提供优质的推广渠道和技术支持。',
    icon: 'ri-file-list-3-line',
  },
  {
    title: '广告投放合作',
    desc: '如果您有与漫画文化相关的品牌或产品希望精准触达年轻读者群体，欢迎洽谈广告投放合作。',
    icon: 'ri-megaphone-line',
  },
  {
    title: '技术合作',
    desc: '我们开放漫画阅读核心技术接口，欢迎APP、小程序、智能硬件等渠道的合作接入。',
    icon: 'ri-code-box-line',
  },
];

export default function AboutPage() {
  const { t } = useTranslation();

  const faqSchemaData = {
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 八仔漫画网',
          description: '了解八仔漫画网品牌故事、平台数据、用户口碑与商务合作联系方式。',
          url: 'http://jisutxt.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '八仔漫画网',
            url: 'http://jisutxt.com/',
          },
          inLanguage: 'zh-CN',
        }}
      />

      <SchemaOrg
        type="Organization"
        data={{
          name: '八仔漫画网',
          alternateName: 'Bazai Manhua',
          url: 'http://jisutxt.com/',
          logo: {
            '@type': 'ImageObject',
            url: 'http://jisutxt.com/logo.png',
          },
          description: '八仔漫画网是国内领先的免费漫画在线阅读平台，提供高品质、无广告的纯净漫画阅读体验。',
          foundingDate: '2020',
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: '客户服务',
              email: 'hello@jisutxt.com',
            },
            {
              '@type': 'ContactPoint',
              contactType: '商务合作',
              email: 'hello@jisutxt.com',
            },
          ],
        }}
      />

      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('breadcrumb.home'),
              item: 'http://jisutxt.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: t('nav.about'),
              item: 'http://jisutxt.com/about',
            },
          ],
        }}
      />

      <SchemaOrg type="FAQPage" data={faqSchemaData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: t('nav.about') },
            ]}
          />

          <section className="relative w-full h-[400px] md:h-[480px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20nook%20with%20manga%20bookshelves%2C%20soft%20golden%20afternoon%20light%20streaming%20through%20window%2C%20comfortable%20armchair%2C%20artistic%20illustration%20style%2C%20relaxing%20atmosphere%2C%20warm%20cream%20and%20amber%20tones%2C%20literary%20aesthetic&width=1920&height=1080&seq=about-hero-manga&orientation=landscape"
              alt="关于八仔漫画网"
              title="八仔漫画网 - 关于我们"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
            <div className="relative z-10 w-full h-full flex items-end px-4 md:px-6 pb-12 md:pb-16">
              <div className="max-w-2xl">
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">
                  {t('about.title')}
                </h1>
                <p className="text-base md:text-lg text-white/80">
                  {t('about.subtitle')}
                </p>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block text-xs font-medium text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full mb-4">
                {t('about.brandIntro')}
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-light text-foreground-900 leading-tight mb-8">
                专注漫画阅读
                <br />
                打造纯净体验
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mb-6">
                {t('about.brandDesc')}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mb-6">
                我们的团队由一群热爱漫画的资深读者和技术爱好者组成，深知读者对于流畅阅读体验的渴望。因此，我们坚持零广告、零付费的运营理念，通过精心的技术优化，确保每一位读者都能享受到快速、清晰、舒适的阅读体验。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                从2020年成立至今，八仔漫画网已经累计服务超过5000万读者，收录漫画作品超过2000部，涵盖热血、玄幻、修真、都市、科幻等多种类型。我们始终坚持以用户为中心，不断优化阅读体验，努力成为国内最受欢迎的漫画阅读平台之一。
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-2">
                平台数据
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                数据见证实力，用心服务每一位读者
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {platformStats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${stat.icon} text-xl text-primary-500`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-sm font-medium text-foreground-700 mb-1">{stat.label}</p>
                    <p className="text-xs text-foreground-500">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-2">
                {t('about.reputation')}
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                来自真实读者的声音，每一份评价都是我们前进的动力
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {testimonials.slice(0, 4).map((testimonial) => (
                  <div key={testimonial.id} className="bg-background-50 rounded-lg p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">{testimonial.name}</p>
                        <p className="text-xs text-foreground-500">{testimonial.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {testimonial.content}
                    </p>
                    <div className="flex items-center gap-0.5 mt-2">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-2">
                商务合作
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                我们欢迎各类形式的商务合作，携手共创漫画生态
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {cooperationItems.map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg p-5 md:p-6">
                    <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                {t('about.contact')}
              </h2>
              <p className="text-sm text-foreground-600 mb-8">
                {t('about.contactDesc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-4 p-5 rounded-lg bg-background-50">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-mail-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t('about.contactEmail')}</p>
                    <p className="text-sm font-medium text-foreground-800">hello@jisutxt.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-5 rounded-lg bg-background-50">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-wechat-line text-xl text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">{t('about.contactWechat')}</p>
                    <p className="text-sm font-medium text-foreground-800">bazaimanhua</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-5 rounded-lg bg-background-50">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-mail-send-line text-xl text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">商务合作邮箱</p>
                    <p className="text-sm font-medium text-foreground-800">hello@jisutxt.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-5 rounded-lg bg-background-50">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-phone-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-1">客服热线</p>
                    <p className="text-sm font-medium text-foreground-800">400-888-6666</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="faq" className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer select-none">
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}